'use client';

import React from 'react';
import { Box, Flex, Text } from '@skin-seoul/design-system';
import {
  useCartService,
  useRegionCurrency,
  type Country,
  CLASSNAME,
  getTiersForUser,
  inferIsFirstOrder,
  buildMilestonesFromTiers,
  calculateProgress,
  getNextMilestoneIndex,
  formatCurrency,
} from '@skin-seoul/service';
import { CheckIcon } from './components/CheckIcon';
import { ConfettiAnimation } from './components/ConfettiAnimation';
import { BMSMGradient } from './components/BMSMGradient';

interface BMSMCampaignBannerProps {
  className?: string;
  /** Banner height in px */
  height?: number;
}

export function BMSMCampaignBanner({ className = '', height = 116 }: BMSMCampaignBannerProps) {
  const { country, currency } = useRegionCurrency();
  const { useGetCartSuspenseQuery } = useCartService();

  // Get cart data
  const { data: cartData } = useGetCartSuspenseQuery({ currency });

  // Extract data
  const isAuthenticated = cartData?.isAuthenticated ?? false;
  const cartTotalPrice = cartData?.cart.totals.totalPrice ?? 0;
  const _isFreeShipping = cartData?.cart.shippingRates?.length !== 0 && cartData?.cart.shippingRates?.[0]?.price === 0;
  const shippingCost = cartData?.cart.shippingRates?.[0]?.price ?? 0;
  const coupons = cartData?.cart.coupons ?? [];

  // Infer if first order
  const isFirstOrder = inferIsFirstOrder(coupons);

  // Get correct tiers with first order inference
  const finalTiers = getTiersForUser({
    country: country as Country,
    isAuthenticated,
    isFirstOrder: isFirstOrder,
  });

  // Build milestones with display config based on authentication status
  const milestonesData = buildMilestonesFromTiers({
    tiers: finalTiers,
    cartTotalPrice,
    currency,
    isAuthenticated,
  });

  // Override free shipping achievement if server says so
  const milestones = milestonesData.map((m) => {
    if (_isFreeShipping && m.tier.type === 'shipping') {
      return {
        ...m,
        achieved: true,
        statusText: 'Available',
        displayConfig: { ...m.displayConfig, active: true },
      };
    }
    return m;
  });

  // Calculate progress
  const baseProgress = calculateProgress({ tiers: finalTiers, cartTotalPrice, isFreeShipping: _isFreeShipping });
  const progress =
    _isFreeShipping && milestones[0]?.tier.type === 'shipping'
      ? Math.max(baseProgress, milestones[0].anchorPercent * 100)
      : baseProgress;

  // Confetti animations
  const showFreeShippingConfetti = milestones[0]?.achieved ?? false;
  const showDiscountConfetti = milestones.find((m) => m.tier.type === 'discount')?.achieved ?? false;

  // Determine which milestone should show savings text (next unachieved, null if all achieved)
  const nextMilestoneIndex = getNextMilestoneIndex(milestones);

  const LABEL_AREA_HEIGHT_PX = 56;
  const ICON_SIZE_PX = 24;
  const BAR_HEIGHT_PX = 4;
  const CHECK_TEXT_GAP_PX = 4;

  return (
    <Flex
      position="relative"
      width="100%"
      direction="column"
      bg="generic.100"
      overflow="hidden"
      alignItems="center"
      justifyContent="center"
      py="16px"
      minH={`${height}px`}
      height={`${height}px`}
      gap={2}
      borderRadius="16px"
      className={className}
    >
      <BMSMGradient />

      <Flex
        zIndex={1}
        position="relative"
        width="100%"
        padding="0 16px"
        height={`${LABEL_AREA_HEIGHT_PX + ICON_SIZE_PX + CHECK_TEXT_GAP_PX}px`}
        alignItems="center"
        justifyContent="center"
        className={CLASSNAME.NOT_TRANSLATE}
      >
        <Box position="absolute" left="0" right="0" bottom="0" height={`${ICON_SIZE_PX}px`}>
          {/* Progress Bar */}
          <Box
            position="absolute"
            left="16px"
            right="16px"
            top={`${(ICON_SIZE_PX - BAR_HEIGHT_PX) / 2}px`}
            height={`${BAR_HEIGHT_PX}px`}
            bg="neutral.600"
            borderRadius="2px"
          >
            <Box
              width={`${progress}%`}
              height="100%"
              bg="success.600"
              borderRadius="2px"
              transition="width 0.3s ease"
            />
          </Box>

          {/* Milestones */}
          <Box
            position="absolute"
            left="16px"
            right="16px"
            top={`-${LABEL_AREA_HEIGHT_PX + CHECK_TEXT_GAP_PX}px`}
            height={`${LABEL_AREA_HEIGHT_PX + ICON_SIZE_PX + CHECK_TEXT_GAP_PX}px`}
          >
            {milestones.map((m, index) => {
              const clamped = Math.max(0, Math.min(1, m.anchorPercent));

              const left = (() => {
                if (clamped === 0) {
                  return '0%';
                }
                if (clamped === 1) {
                  return '100%';
                }
                return `${clamped * 100}%`;
              })();

              const translate = (() => {
                if (clamped === 0) {
                  return 'translateX(0)';
                }
                if (clamped === 1) {
                  return 'translateX(-100%)';
                }
                return 'translateX(-50%)';
              })();

              const iconSize = m.displayConfig.iconVariant === 'dot' ? '12px' : '24px';
              // Adjust bottom position for dot variant to keep it vertically centered on the progress bar
              const iconBottom = m.displayConfig.iconVariant === 'dot' ? '6px' : '0';

              // Opacity logic:
              // - Not achieved: full opacity (show threshold amount)
              // - Achieved + Active: full opacity (benefit is applied)
              // - Achieved + Not Active: 0.2 opacity (achieved but not applied due to no stacking for guests)
              // - Achieved + Superseded by a higher tier milestone: 0.2 opacity
              const isSupersededByHigherTier =
                m.achieved &&
                milestones.slice(index + 1).some((other) => other.achieved && other.tier.type === m.tier.type);
              const textOpacity = (m.achieved && !m.displayConfig.active) || isSupersededByHigherTier ? 0.2 : 1;

              const isShipping = m.tier.type === 'shipping';
              const savingsAmount = isShipping ? shippingCost : m.savingsAmount;
              const showSavings = isShipping ? !m.achieved && shippingCost > 0 : nextMilestoneIndex === index && m.savingsAmount > 0;
              const savingsVisibility = showSavings ? 'visible' : 'hidden';

              return (
                <React.Fragment key={`${m.tier.type}-${index}`}>
                  <Flex
                    position="absolute"
                    left={left}
                    transform={translate}
                    bottom={`${ICON_SIZE_PX + CHECK_TEXT_GAP_PX}px`}
                    direction="column"
                    gap="2px"
                    align="center"
                    opacity={textOpacity}
                  >
                    <Text
                      textStyle="label.small"
                      whiteSpace="nowrap"
                      fontWeight="medium"
                      color={m.achieved ? 'primary.200' : 'neutral.400'}
                    >
                      {m.achieved ? m.statusText : `Spend ${m.statusText}`}
                    </Text>
                    <Text
                      textStyle="label.large"
                      whiteSpace="nowrap"
                      fontWeight="bold"
                      color="generic.0"
                      letterSpacing="-0.64px"
                    >
                      {m.title}
                    </Text>
                    <Text
                      textStyle="label.small"
                      whiteSpace="nowrap"
                      fontWeight="semibold"
                      color="#FFFF27"
                      visibility={savingsVisibility}
                    >
                      Save {formatCurrency(currency, savingsAmount)}
                    </Text>
                  </Flex>
                  <Box
                    position="absolute"
                    left={left}
                    transform={translate}
                    bottom={iconBottom}
                    width={iconSize}
                    height={iconSize}
                    opacity={textOpacity}
                  >
                    <CheckIcon achieved={m.achieved} variant={m.displayConfig.iconVariant} />
                  </Box>
                </React.Fragment>
              );
            })}
          </Box>
        </Box>
      </Flex>

      <ConfettiAnimation
        showFreeShippingConfetti={showFreeShippingConfetti}
        showDiscountConfetti={showDiscountConfetti}
      />
    </Flex>
  );
}
